// AKA Initial call webhook (PRE)
import React, { useEffect, useState } from 'react'
import { Card, CardBody, Col, Row, Label, Input, Button, Spinner, UncontrolledTooltip } from 'reactstrap';
import { delWebhookConfiguration, getWebhookConfiguration, postWebhookConfiguration, putWebhookConfiguration } from '../../helper/backend-methods';
import { showToast } from '../../components/Toast';
import { JsonView, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';

const WebhookConfig = () => {
    const [configurations, setConfigurations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submittingIds, setSubmittingIds] = useState({});
    const [deletingIds, setDeletingIds] = useState({});
    const [editingIds, setEditingIds] = useState({});
    const [liveJsonValue, setLiveJsonValue] = useState("");
    const [activePreviewIndex, setActivePreviewIndex] = useState(null);
    const [errors, setErrors] = useState({});
    const [editingJsonIndex, setEditingJsonIndex] = useState(null);
    const [editingJsonValue, setEditingJsonValue] = useState('');

    useEffect(() => {
        fetchConfigurations();
    }, []);

    const generateApiKey = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = 'wr_live_';
        for (let i = 0; i < 24; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    };

    const handleGenerateApiKey = (index) => {
        const newApiKey = generateApiKey();
        handleInputChange(index, 'apiKey', newApiKey);
    };

    const defaultPayload = {
        phone: "+1234567890",
        name: "John Doe"
    };

    const fetchConfigurations = async () => {
        setLoading(true);
        try {
            const data = await getWebhookConfiguration();
            if (data?.status && data.data) {
                const configs = Array.isArray(data.data) ? data.data : [data.data];
                const preConfigs = configs.filter(config => config.webhookType === "PRE");
                const formattedConfigs = preConfigs.map(config => ({
                    ...config,
                    payload: config.payload ? parseJsonPayload(config.payload) : defaultPayload
                }));
                setConfigurations(formattedConfigs.length ? formattedConfigs : [createEmptyConfig()]);
            } else {
                setConfigurations([createEmptyConfig()]);
            }
        } catch (err) {
            console.error("Fetch failed:", err);
            showToast("error", "Request Failed", err.message);
            setConfigurations([createEmptyConfig()]);
        } finally {
            setLoading(false);
        }
    };

    const parseJsonPayload = (str) => {
        try {
            return typeof str === 'string' ? JSON.parse(str) : str;
        } catch {
            return defaultPayload;
        }
    };

    const validateJsonPhone = (jsonObj) => {
        if (!jsonObj || typeof jsonObj !== 'object') {
            return "Invalid JSON format";
        }
        if (!jsonObj.phone) {
            return "Phone key is required in JSON payload";
        }
        return null;
    };

    const createEmptyConfig = () => ({
        _id: Date.now() + Math.random(),
        apiKey: "",
        endPoint: process.env.REACT_APP_WEBHOOK_ENDPOINTS || "",
        title: "",
        payload: { ...defaultPayload },
        isDefault: false,
        isNew: true
    });

    const validateConfig = (config, index) => {
        const newErrors = { ...errors };
        const configErrors = {};

        if (!config.apiKey?.trim()) {
            configErrors.apiKey = "API Key is required";
        }
        if (!config.endPoint?.trim()) {
            configErrors.endPoint = "End Point is required";
        }
        if (!config.title?.trim()) {
            configErrors.title = "Title is required";
        }

        // Validate JSON and phone key
        const jsonError = validateJsonPhone(config.payload);
        if (jsonError) {
            configErrors.payload = jsonError;
        }

        if (Object.keys(configErrors).length > 0) {
            newErrors[index] = configErrors;
        } else {
            delete newErrors[index];
        }

        setErrors(newErrors);
        return Object.keys(configErrors).length === 0;
    };

    const handleInputChange = (index, field, value) => {
        setConfigurations(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };

            if (!updated[index].isNew && !editingIds[index]) {
                setEditingIds(prev => ({ ...prev, [index]: true }));
            }

            return updated;
        });

        if (errors[index]?.[field]) {
            setErrors(prev => ({
                ...prev,
                [index]: { ...prev[index], [field]: "" }
            }));
        }
    };

    const handleJsonEdit = (index) => {
        try {
            const parsed = JSON.parse(editingJsonValue);
            setConfigurations(prev => {
                const updated = [...prev];
                updated[index] = { ...updated[index], payload: parsed };

                if (!updated[index].isNew && !editingIds[index]) {
                    setEditingIds(prev => ({ ...prev, [index]: true }));
                }

                return updated;
            });
            setEditingJsonIndex(null);
            setEditingJsonValue('');
            setActivePreviewIndex(null);
            setLiveJsonValue('');

            if (errors[index]?.payload) {
                setErrors(prev => ({
                    ...prev,
                    [index]: { ...prev[index], payload: "" }
                }));
            }
        } catch (e) {
            showToast("error", "Invalid JSON", "Please enter valid JSON");
        }
    };

    const handleSubmit = async (index, actionType = "new", id = "") => {
        const config = configurations[index];

        if (!validateConfig(config, index)) {
            showToast("error", "Validation Failed", "Please fill all required fields and ensure valid JSON with phone key");
            return;
        }

        setSubmittingIds(prev => ({ ...prev, [index]: true }));

        try {
            const formData = {
                apiKey: config.apiKey,
                endPoint: config.endPoint,
                title: config.title,
                payload: JSON.stringify(config.payload),
                isDefault: config.isDefault || false,
                webhookType: "PRE"
            };

            const { data } = await (actionType === "new" ? postWebhookConfiguration(formData) : putWebhookConfiguration(formData, id));

            if (data?._id) {
                showToast("success", "Configuration Saved", "Webhook configuration saved successfully");

                setConfigurations(prev => {
                    const updated = [...prev];
                    updated[index] = {
                        ...updated[index],
                        isNew: false,
                        id: data.Data?.id || updated[index].id
                    };
                    return updated;
                });

                setEditingIds(prev => {
                    const updated = { ...prev };
                    delete updated[index];
                    return updated;
                });

                fetchConfigurations();
            } else {
                showToast("error", "Save Failed", data?.Message || "Could not save configuration");
            }
        } catch (err) {
            console.error("Submit failed:", err);
            showToast("error", "Save Failed", err.message);
        } finally {
            setSubmittingIds(prev => ({ ...prev, [index]: false }));
        }
    };

    const handleDelete = async (index, configId) => {
        const config = configurations[index];

        if (config.isNew) {
            setConfigurations(prev => prev.filter((_, i) => i !== index));
            return;
        }

        setDeletingIds(prev => ({ ...prev, [index]: true }));

        try {
            const { data } = await delWebhookConfiguration(configId);
            setConfigurations(prev => prev.filter((_, i) => i !== index));
            showToast("success", "Configuration Deleted", "Webhook configuration removed");
            fetchConfigurations();
        } catch (err) {
            console.error("Delete failed:", err);
            showToast("error", "Delete Failed", err.message);
        } finally {
            setDeletingIds(prev => ({ ...prev, [index]: false }));
        }
    };

    const handleCancel = (index) => {
        fetchConfigurations();
        setEditingIds(prev => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
        });
    };

    const handleAddNew = () => {
        setConfigurations(prev => [...prev, createEmptyConfig()]);
    };

    const generateCurlCommand = (config) => {
        try {
            const url = config.endPoint || '';
            const apiKey = config.apiKey || '';
            const payload = config.payload || {};

            // Convert payload to JSON string
            const jsonPayload = JSON.stringify(payload, null, 2);

            // Build curl command
            return `curl -X POST "${url}" \\
      -H "Content-Type: application/json" \\
      -H "Authorization: ${apiKey}" \\
      -d '${jsonPayload.replace(/'/g, "'\\''")}'`;
        } catch (error) {
            return "Error generating cURL command";
        }
    };

    const getPreviewPayload = (index) => {
        // If this index is being edited and has live value, use that
        if (activePreviewIndex === index && liveJsonValue) {
            try {
                return JSON.parse(liveJsonValue);
            } catch {
                // If invalid JSON, return current config payload
                return configurations[index]?.payload || defaultPayload;
            }
        }
        // Otherwise use the saved config
        return configurations[index]?.payload || defaultPayload;
    };

    const generateAndCopyCurl = (config) => {
        try {
            // Build the curl command
            const url = config.endPoint || '';
            const apiKey = config.apiKey || '';
            const payload = config.payload || {};

            // Convert payload to JSON string
            const jsonPayload = JSON.stringify(payload, null, 2);

            // Build curl command with proper escaping
            const curlCommand = `curl -X POST "${url}" \\
        -H "Content-Type: application/json" \\
        -H "Authorization: ${apiKey}" \\
        -d '${jsonPayload.replace(/'/g, "'\\''")}'`;

            // Copy to clipboard
            navigator.clipboard.writeText(curlCommand).then(() => {
                showToast("success", "Copied!", "cURL command copied to clipboard");
            }).catch(() => {
                // Fallback for older browsers
                const textarea = document.createElement('textarea');
                textarea.value = curlCommand;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                showToast("success", "Copied!", "cURL command copied to clipboard");
            });
        } catch (error) {
            console.error('Error generating curl:', error);
            showToast("error", "Copy Failed", "Could not generate cURL command");
        }
    };

    if (loading) {
        return (
            <div className="content-page">
                <div className="content">
                    <div className="container-fluid text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-3">Loading configurations...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="content-page">
            <div className="content">
                <div className="container-fluid">
                    <div className="py-3 row">
                        <div className="col-12">
                            <Row className="my-2 align-items-center">
                                <Col md={8}>
                                    <h4 className='fs-18'>Initiate Call Webhook</h4>
                                    <p className='text-muted fs-12'>Initiate call webhook configurations</p>
                                </Col>
                                {/* <Col md={4} className="text-end">
                                    <Button
                                        color="primary"
                                        onClick={handleAddNew}
                                        className="px-4 py-2 rounded linear-gradient-btn">
                                        Add New Configuration
                                    </Button>
                                </Col> */}
                            </Row>
                        </div>
                    </div>

                    {configurations.map((config, index) => (
                        <Row key={config._id || index} className="mb-4">
                            <Col md={12}>
                                <Card className='bg-white rounded-4 shadow-sm'>
                                    <CardBody>
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <div className="d-flex align-items-center gap-3">
                                                <h5 className="mb-0">
                                                    Configuration #{index + 1}
                                                </h5>
                                                {config.isDefault && (
                                                    <span className="badge bg-primary">
                                                        <i className="ri-star-fill me-1"></i> Default
                                                    </span>
                                                )}
                                                {config.isNew && (
                                                    <span className="badge bg-warning">
                                                        <i className="ri-file-copy-line me-1"></i> New
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                {/* <Button
                                                    color="primary"
                                                    size="sm"
                                                    outline
                                                    id={`copyCurl${index}`}
                                                    onClick={() => generateAndCopyCurl(config)}
                                                    disabled={submittingIds[index] || deletingIds[index]}
                                                    className="d-inline-flex align-items-center gap-1 me-2"
                                                >
                                                    <i className="ri-file-copy-line"></i>
                                                </Button>
                                                <UncontrolledTooltip placement="top" target={`copyCurl${index}`}>
                                                    Copy as cURL
                                                </UncontrolledTooltip> */}
                                                <Button
                                                    color="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(index, config._id)}
                                                    disabled={deletingIds[index] || submittingIds[index]}
                                                    className="d-inline-flex align-items-center gap-1"
                                                >
                                                    {deletingIds[index] ? (
                                                        <Spinner size="sm" />
                                                    ) : (
                                                        <i className="ri-delete-bin-line"></i>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>

                                        <Row>
                                            <Col md={6} className='mb-3'>
                                                <Label className="fw-bold">
                                                    Title
                                                </Label>
                                                <Input
                                                    type="text"
                                                    placeholder="Webhook Title"
                                                    value={config.title || ''}
                                                    onChange={(e) => handleInputChange(index, 'title', e.target.value)}
                                                    invalid={!!errors[index]?.title}
                                                    disabled={submittingIds[index] || deletingIds[index]}
                                                    className='custom-input'
                                                />
                                                {errors[index]?.title && (
                                                    <small className="text-danger">{errors[index].title}</small>
                                                )}
                                            </Col>

                                            <Col md={6} className='mb-3'>
                                                <Label className="fw-bold">
                                                    API Key
                                                </Label>
                                                <div className="d-flex gap-2">
                                                    <Input
                                                        type="text"
                                                        placeholder="sk_live_xxxxxxxxx"
                                                        value={config.apiKey || ''}
                                                        onChange={(e) => handleInputChange(index, 'apiKey', e.target.value)}
                                                        invalid={!!errors[index]?.apiKey}
                                                        disabled={submittingIds[index] || deletingIds[index]}
                                                        className='custom-input'
                                                    />
                                                    <button
                                                        size="sm"
                                                        id={`generateKey${index}`}
                                                        onClick={() => handleGenerateApiKey(index)}
                                                        disabled={submittingIds[index] || deletingIds[index]}
                                                        className="d-inline-flex align-items-center justify-content-center px-3 py-1 rounded linear-gradient-btn"
                                                    >
                                                        <i className="ri-refresh-line text-white"></i>
                                                    </button>
                                                    <UncontrolledTooltip placement="top" style={{ minWidth: "100px" }} popperClassName="text-left" innerClassName="text-left" target={`generateKey${index}`}>
                                                        <p className='m-0 text-left'>Generate API Key</p>
                                                    </UncontrolledTooltip>
                                                </div>
                                                {errors[index]?.apiKey && (
                                                    <small className="text-danger">{errors[index].apiKey}</small>
                                                )}
                                            </Col>


                                            <Col md={12} className='mb-3'>
                                                <Label className="fw-bold">
                                                    Endpoint URL
                                                </Label>
                                                <Input
                                                    type="url"
                                                    readOnly={true}
                                                    placeholder="https://example.com/webhook"
                                                    value={config.endPoint || ''}
                                                    onChange={(e) => handleInputChange(index, 'endPoint', e.target.value)}
                                                    invalid={!!errors[index]?.endPoint}
                                                    disabled={submittingIds[index] || deletingIds[index]}
                                                    className='custom-input'
                                                />
                                                {errors[index]?.endPoint && (
                                                    <small className="text-danger">{errors[index].endPoint}</small>
                                                )}
                                            </Col>

                                            <Col md={12} className='mb-3'>
                                                <Label className="fw-bold">
                                                    Payload (JSON) <small className="text-muted">- Must contain "phone" key</small>
                                                </Label>
                                                <div className="code-preview border rounded">
                                                    {editingJsonIndex === index ? (
                                                        <div className="p-3">
                                                            <Input
                                                                type="textarea"
                                                                rows="6"
                                                                value={editingJsonValue}
                                                                onChange={(e) => { setEditingJsonValue(e.target.value); setActivePreviewIndex(index); setLiveJsonValue(e.target.value); }}
                                                                className="font-monospace"
                                                                placeholder="Enter valid JSON"
                                                            />
                                                            <div className="d-flex gap-2 mt-2">
                                                                <Button
                                                                    size="sm"
                                                                    color="primary"
                                                                    className='linear-gradient-btn'
                                                                    onClick={() => handleJsonEdit(index)}
                                                                >
                                                                    Save
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    color="outline-primary"
                                                                    onClick={() => {
                                                                        setEditingJsonIndex(null);
                                                                        setEditingJsonValue('');
                                                                        setActivePreviewIndex(null);
                                                                        setLiveJsonValue('');
                                                                    }}
                                                                >
                                                                    <span>Cancel</span>
                                                                </Button>

                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="p-3">
                                                            <div className="d-flex justify-content-end mb-2">
                                                                <Button
                                                                    size="sm"
                                                                    color="primary"
                                                                    outline
                                                                    onClick={() => {
                                                                        setEditingJsonIndex(index);
                                                                        setEditingJsonValue(JSON.stringify(config.payload, null, 2));
                                                                    }}
                                                                >
                                                                    <i className="ri-edit-line me-1"></i> Edit JSON
                                                                </Button>
                                                            </div>
                                                            <JsonView
                                                                data={config.payload || defaultPayload}
                                                                style={defaultStyles}
                                                                shouldExpandNode={() => true}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                {errors[index]?.payload && (
                                                    <small className="text-danger">{errors[index].payload}</small>
                                                )}
                                                <small className="text-muted d-flex align-items-center gap-1 mt-1">
                                                    <i className="ri-information-line"></i>
                                                    Phone key is required. Click "Edit JSON" to modify the payload.
                                                </small>
                                            </Col>

                                            <Col md={12} className='mb-3'>
                                                <Row className='mb-2'>
                                                    <Col md={6}>
                                                        <Label className="fw-bold">
                                                            CURL (Preview)
                                                        </Label>
                                                    </Col>
                                                    <Col md={6} className='text-right'>
                                                        <Button
                                                            color="primary"
                                                            size="sm"
                                                            outline
                                                            id={`copyCurl${index}`}
                                                            onClick={() => generateAndCopyCurl(config)}
                                                            disabled={submittingIds[index] || deletingIds[index]}
                                                            className="d-inline-flex align-items-center justify-content-end gap-1 me-2"
                                                        >
                                                            <i className="ri-file-copy-line"></i>
                                                        </Button>
                                                        <UncontrolledTooltip placement="top" target={`copyCurl${index}`}>
                                                            Copy as cURL
                                                        </UncontrolledTooltip>
                                                    </Col>
                                                </Row>


                                                <div className="code-preview border rounded">
                                                    <pre className="m-0 p-2">
                                                        {(() => {
                                                            const previewConfig = {
                                                                ...config,
                                                                payload: getPreviewPayload(index)
                                                            };
                                                            return generateCurlCommand(previewConfig);
                                                        })()}
                                                    </pre>
                                                </div>
                                            </Col>

                                            <Col md={6} className='mb-3'>
                                                <div className="d-flex align-items-center gap-3">
                                                    <Label className="fw-bold mb-0">
                                                        <i className="ri-star-line me-1"></i> Set as Default
                                                    </Label>
                                                    <label className="gradient-checkbox-wrapper d-flex align-items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={config.isDefault || false}
                                                            onChange={(e) => handleInputChange(index, 'isDefault', e.target.checked)}
                                                            disabled={submittingIds[index] || deletingIds[index]}
                                                            className="gradient-checkbox"
                                                        />
                                                        <span className="checkbox-label">
                                                            {config.isDefault ? "Yes" : "No"}
                                                        </span>
                                                    </label>
                                                </div>
                                            </Col>
                                        </Row>

                                        <Row className="mt-4">
                                            <Col md={12} className="d-flex justify-content-end gap-2">
                                                {editingIds[index] && (
                                                    <button
                                                        onClick={() => handleCancel(index)}
                                                        disabled={submittingIds[index]}
                                                        className="px-4 py-2 rounded me-2 outline-gradient-btn"
                                                    >
                                                        <span>Cancel</span>
                                                    </button>
                                                )}
                                                <Button
                                                    color="primary"
                                                    onClick={() => handleSubmit(index, config.isNew ? 'new' : 'update', config._id)}
                                                    disabled={submittingIds[index] || deletingIds[index] || editingJsonIndex === index}
                                                    className="d-inline-flex px-4 py-2 rounded linear-gradient-btn"
                                                >
                                                    {submittingIds[index] ? (
                                                        <>
                                                            <Spinner size="sm" />
                                                            Saving...
                                                        </>
                                                    ) : (
                                                        <>
                                                            {config.isNew ? 'Save' : 'Update'}
                                                        </>
                                                    )}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>

                        </Row>
                    ))}


                    {configurations.length > 0 && (
                        <Row>
                            <Col md={12} className='text-right mb-4'>
                                <Button
                                    color="primary"
                                    onClick={handleAddNew}
                                    className="px-4 py-2 rounded linear-gradient-btn">
                                    Add New Configuration
                                </Button>
                            </Col>
                        </Row>
                    )}
                    {configurations.length === 0 && (
                        <Row>
                            <Col md={12}>
                                <Card className="bg-light border-0">
                                    <CardBody className="text-center py-5">
                                        <i className="ri-webhook-line fs-1 text-muted mb-3"></i>
                                        <p className="text-muted mb-3">No configurations found</p>
                                        <Button color="primary" onClick={handleAddNew} className="d-inline-flex align-items-center gap-2">
                                            <i className="ri-add-line"></i> Add Your First Configuration
                                        </Button>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WebhookConfig;